import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Plus, Calendar, Clock, Trash2, BookOpen, AlertTriangle,
  Target, TrendingUp, ChevronRight, CheckCircle, Zap, Edit2, X
} from 'lucide-react';

const subjectIcons = { 
  Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', 
  Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬'
};

const subjectColors = {
  Math: 'violet', Science: 'cyan', English: 'amber', 'Social Science': 'emerald',
  Hindi: 'orange', EVS: 'green', Physics: 'blue', Chemistry: 'pink', Biology: 'teal'
};

export default function ExamCalendar({ onNavigate }) {
  const { api, user } = useAuth();
  const [subjectExams, setSubjectExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  const [newExam, setNewExam] = useState({ 
    subject: '', 
    exam_name: '',
    exam_date: '', 
    syllabus_chapters: [],
    target_score: 90,
    notes: '' 
  });

  const [availableChapters, setAvailableChapters] = useState([]);

  // Get subjects based on class
  const getSubjectsForClass = () => {
    const classNum = parseInt(user?.class_name?.replace('Class ', '') || '10');
    if (classNum >= 11) {
      return ['Math', 'Physics', 'Chemistry', 'Biology', 'English'];
    }
    return ['Math', 'Science', 'English', 'Social Science', 'Hindi', 'EVS'];
  };

  const subjects = getSubjectsForClass();

  const fetchSubjectExams = useCallback(async () => {
    try {
      const res = await api.get('/exams/subject-wise');
      setSubjectExams(res.data);
    } catch (err) { 
      console.error(err);
      // Fallback to regular exams endpoint
      try {
        const res = await api.get('/exams');
        setSubjectExams(res.data);
      } catch (e) { console.error(e); }
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSubjectExams(); }, [fetchSubjectExams]);

  // Fetch chapters when subject changes
  useEffect(() => {
    if (newExam.subject) {
      const fetchChapters = async () => {
        try {
          const res = await api.get(`/chapters?subject=${newExam.subject}&class_name=${user?.class_name || 'Class 10'}`);
          setAvailableChapters(res.data);
        } catch (err) { console.error(err); }
      };
      fetchChapters();
    }
  }, [api, newExam.subject, user?.class_name]);

  const addOrUpdateExam = async () => {
    if (!newExam.subject || !newExam.exam_date) return;
    
    try {
      const examData = {
        subject: newExam.subject,
        exam_name: newExam.exam_name || `${newExam.subject} Exam`,
        exam_date: new Date(newExam.exam_date).toISOString(),
        syllabus_chapters: newExam.syllabus_chapters,
        target_score: newExam.target_score,
        notes: newExam.notes || null,
      };

      if (editingExam) {
        await api.put(`/exams/subject-wise/${editingExam.id}`, examData);
      } else {
        await api.post('/exams/subject-wise', examData);
      }
      
      setNewExam({ subject: '', exam_name: '', exam_date: '', syllabus_chapters: [], target_score: 90, notes: '' });
      setShowAdd(false);
      setEditingExam(null);
      fetchSubjectExams();
    } catch (err) { console.error(err); }
  };

  const deleteExam = async (examId) => {
    if (!confirm('Delete this exam?')) return;
    try {
      await api.delete(`/exams/subject-wise/${examId}`);
      fetchSubjectExams();
    } catch (err) { console.error(err); }
  };

  const startEdit = (exam) => {
    setEditingExam(exam);
    setNewExam({
      subject: exam.subject,
      exam_name: exam.exam_name || exam.name,
      exam_date: exam.exam_date?.split('T')[0] || exam.date?.split('T')[0],
      syllabus_chapters: exam.syllabus_chapters || [],
      target_score: exam.target_score || 90,
      notes: exam.notes || ''
    });
    setShowAdd(true);
  };

  const toggleChapter = (chapterNum) => {
    setNewExam(prev => ({
      ...prev,
      syllabus_chapters: prev.syllabus_chapters.includes(chapterNum) 
        ? prev.syllabus_chapters.filter(c => c !== chapterNum)
        : [...prev.syllabus_chapters, chapterNum]
    }));
  };

  const getUrgencyLevel = (daysLeft) => {
    if (daysLeft <= 3) return { color: 'red', label: 'Critical', bg: 'bg-red-500/10 border-red-500/30' };
    if (daysLeft <= 7) return { color: 'orange', label: 'Urgent', bg: 'bg-orange-500/10 border-orange-500/30' };
    if (daysLeft <= 14) return { color: 'amber', label: 'Soon', bg: 'bg-amber-500/10 border-amber-500/30' };
    if (daysLeft <= 30) return { color: 'cyan', label: 'Upcoming', bg: 'bg-cyan-500/10 border-cyan-500/30' };
    return { color: 'slate', label: 'Scheduled', bg: 'bg-slate-800/50 border-slate-700' };
  };

  const upcomingExams = subjectExams.filter(e => e.days_left >= 0).sort((a, b) => a.days_left - b.days_left);
  const pastExams = subjectExams.filter(e => e.days_left < 0).sort((a, b) => b.days_left - a.days_left);

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-from-calendar" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white font-['Outfit']">Subject Exam Calendar</h1>
              <p className="text-xs text-slate-500">Track exams & progress by subject</p>
            </div>
          </div>
          <button 
            data-testid="add-exam-toggle" 
            onClick={() => { setShowAdd(!showAdd); setEditingExam(null); setNewExam({ subject: '', exam_name: '', exam_date: '', syllabus_chapters: [], target_score: 90, notes: '' }); }}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl w-9 h-9 flex items-center justify-center"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2 rounded-lg font-medium text-sm transition-all ${
              activeTab === 'upcoming' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400'
            }`}
          >
            Upcoming ({upcomingExams.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 py-2 rounded-lg font-medium text-sm transition-all ${
              activeTab === 'past' ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400'
            }`}
          >
            Past ({pastExams.length})
          </button>
        </div>

        {/* Add/Edit Exam Form */}
        <AnimatePresence>
          {showAdd && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 mb-5 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">{editingExam ? 'Edit Exam' : 'Add Subject Exam'}</h3>
                <button onClick={() => { setShowAdd(false); setEditingExam(null); }} className="text-slate-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Subject Selection */}
              <div>
                <label className="text-xs text-slate-500 mb-2 block">Subject *</label>
                <div className="grid grid-cols-3 gap-2">
                  {subjects.map(s => (
                    <button
                      key={s}
                      onClick={() => setNewExam(p => ({ ...p, subject: s, syllabus_chapters: [] }))}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        newExam.subject === s 
                          ? `bg-${subjectColors[s]}-500/20 border-${subjectColors[s]}-500` 
                          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <span className="text-xl block mb-1">{subjectIcons[s]}</span>
                      <span className={`text-xs ${newExam.subject === s ? 'text-white' : 'text-slate-400'}`}>{s}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Exam Name & Date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Exam Name</label>
                  <input 
                    type="text" 
                    value={newExam.exam_name} 
                    onChange={e => setNewExam(p => ({ ...p, exam_name: e.target.value }))}
                    placeholder="e.g. Mid-Term"
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl h-10 px-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">Exam Date *</label>
                  <input 
                    type="date" 
                    value={newExam.exam_date} 
                    onChange={e => setNewExam(p => ({ ...p, exam_date: e.target.value }))}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl h-10 px-3 text-sm text-slate-100 outline-none focus:border-violet-500 [color-scheme:dark]"
                  />
                </div>
              </div>

              {/* Target Score */}
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">Target Score: {newExam.target_score}%</label>
                <input 
                  type="range" 
                  min="50" 
                  max="100" 
                  value={newExam.target_score}
                  onChange={e => setNewExam(p => ({ ...p, target_score: parseInt(e.target.value) }))}
                  className="w-full accent-violet-500"
                />
              </div>

              {/* Syllabus Chapters */}
              {newExam.subject && availableChapters.length > 0 && (
                <div>
                  <label className="text-xs text-slate-500 mb-2 block">
                    Syllabus Chapters ({newExam.syllabus_chapters.length} selected)
                  </label>
                  <div className="max-h-40 overflow-y-auto space-y-1 bg-slate-950/30 rounded-xl p-2">
                    {availableChapters.map(ch => (
                      <button
                        key={ch.number}
                        onClick={() => toggleChapter(ch.number)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between ${
                          newExam.syllabus_chapters.includes(ch.number)
                            ? 'bg-violet-600/20 text-white'
                            : 'hover:bg-slate-800 text-slate-400'
                        }`}
                      >
                        <span>Ch {ch.number}: {ch.name}</span>
                        {newExam.syllabus_chapters.includes(ch.number) && (
                          <CheckCircle className="w-4 h-4 text-violet-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <textarea 
                value={newExam.notes} 
                onChange={e => setNewExam(p => ({ ...p, notes: e.target.value }))}
                placeholder="Notes (optional)" 
                rows={2}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-violet-500 resize-none"
              />

              <button 
                onClick={addOrUpdateExam}
                disabled={!newExam.subject || !newExam.exam_date}
                className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-xl h-11 text-sm font-semibold transition-all"
              >
                {editingExam ? 'Update Exam' : 'Add Exam'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Exam List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {(activeTab === 'upcoming' ? upcomingExams : pastExams).map((exam, i) => {
              const urgency = getUrgencyLevel(exam.days_left);
              const color = subjectColors[exam.subject] || 'violet';
              const progress = exam.readiness_score || exam.progress || 0;
              
              return (
                <motion.div 
                  key={exam.id} 
                  initial={{ opacity: 0, y: 10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ delay: i * 0.05 }}
                  className={`bg-slate-900/50 border rounded-2xl p-4 ${urgency.bg}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-${color}-500/20 flex items-center justify-center text-2xl`}>
                        {subjectIcons[exam.subject] || '📚'}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{exam.subject}</h3>
                        <p className="text-xs text-slate-400">{exam.exam_name || exam.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => startEdit(exam)} className="text-slate-500 hover:text-white p-1">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteExam(exam.id)} className="text-slate-500 hover:text-red-400 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Date & Countdown */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">
                        {new Date(exam.exam_date || exam.date).toLocaleDateString('en-IN', { 
                          weekday: 'short', day: 'numeric', month: 'short' 
                        })}
                      </span>
                    </div>
                    {exam.days_left >= 0 && (
                      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full bg-${urgency.color}-500/20`}>
                        {exam.days_left <= 7 && <AlertTriangle className={`w-3 h-3 text-${urgency.color}-400`} />}
                        <Clock className={`w-3 h-3 text-${urgency.color}-400`} />
                        <span className={`text-xs font-bold text-${urgency.color}-400`}>
                          {exam.days_left === 0 ? 'Today!' : `${exam.days_left}d left`}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-slate-500">Readiness</span>
                      <span className={`font-bold text-${progress >= 70 ? 'emerald' : progress >= 50 ? 'amber' : 'red'}-400`}>
                        {progress}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5 }}
                        className={`h-full rounded-full ${
                          progress >= 70 ? 'bg-emerald-500' : progress >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                      />
                    </div>
                    {exam.target_score && (
                      <p className="text-xs text-slate-500 mt-1">Target: {exam.target_score}%</p>
                    )}
                  </div>

                  {/* Syllabus Chapters */}
                  {exam.syllabus_chapters?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 mb-1.5">
                        Syllabus: {exam.syllabus_chapters.length} chapters
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {exam.syllabus_chapters.slice(0, 5).map(ch => (
                          <span key={ch} className="text-[10px] bg-slate-800/60 text-slate-400 px-2 py-0.5 rounded-full">
                            Ch {ch}
                          </span>
                        ))}
                        {exam.syllabus_chapters.length > 5 && (
                          <span className="text-[10px] bg-slate-800/60 text-slate-400 px-2 py-0.5 rounded-full">
                            +{exam.syllabus_chapters.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Study Recommendation */}
                  {exam.days_left > 0 && exam.days_left <= 30 && (
                    <button
                      onClick={() => onNavigate('daily-loop', { subject: exam.subject })}
                      className={`w-full bg-${color}-500/10 hover:bg-${color}-500/20 border border-${color}-500/20 rounded-xl py-2.5 flex items-center justify-center gap-2 transition-all`}
                    >
                      <Zap className={`w-4 h-4 text-${color}-400`} />
                      <span className={`text-sm font-medium text-${color}-300`}>
                        Practice {exam.subject} Now
                      </span>
                      <ChevronRight className={`w-4 h-4 text-${color}-400`} />
                    </button>
                  )}

                  {exam.notes && (
                    <p className="text-xs text-slate-500 mt-2 italic">📝 {exam.notes}</p>
                  )}
                </motion.div>
              );
            })}

            {/* Empty State */}
            {(activeTab === 'upcoming' ? upcomingExams : pastExams).length === 0 && (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400">
                  {activeTab === 'upcoming' ? 'No upcoming exams' : 'No past exams'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {activeTab === 'upcoming' ? 'Tap + to add subject-wise exams' : ''}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        {upcomingExams.length > 0 && activeTab === 'upcoming' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 bg-slate-900/30 border border-slate-800 rounded-xl p-4"
          >
            <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-violet-400" /> Exam Overview
            </h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-violet-400">{upcomingExams.length}</p>
                <p className="text-xs text-slate-500">Exams</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">
                  {upcomingExams.filter(e => e.days_left <= 7).length}
                </p>
                <p className="text-xs text-slate-500">This Week</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-400">
                  {Math.round(upcomingExams.reduce((a, e) => a + (e.readiness_score || e.progress || 0), 0) / upcomingExams.length) || 0}%
                </p>
                <p className="text-xs text-slate-500">Avg Ready</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
